import React, { Fragment, useState } from 'react';
import { Grid, Switch, Typography, List, ListItem, Divider } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import TableSearchBar from 'components/common/search/TableSearchBar';
import {
  getAllFeatureFlagsForBusinessId,
  updateLaunchDarklyFlag,
} from 'api/admin/businesses/businesses';
import { useGetFeatureFlagsForBusiness } from 'api/queries/useGetFeatureFlagsForBusiness';
import styles from './FeatureFlags.module.scss';

const IndividualBusinessFeatureFlags = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState();
  const [flagFilter, setFlagFilter] = useState('');

  const [localFlagStates, setLocalFlagStates] = useState({});

  const { data: featureFlags, isLoading: isLoadingFeatureFlags } = useGetFeatureFlagsForBusiness({
    businessId: id,
  });

  const saveLaunchDarklyFeatureFlagValue = useMutation({
    mutationFn: ({ flagKey, value }) => {
      return updateLaunchDarklyFlag({
        businessId: id,
        flagKey,
        value,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getFeatureFlagsForBusiness', { businessId: id }] });
    },
    onError: (error, variables) => {
      setError(error?.response?.data?.error);
      // Revert local state on error
      setLocalFlagStates((prev) => ({
        ...prev,
        [variables.flagKey]: undefined,
      }));
    },
  });

  if (isLoadingFeatureFlags) {
    return <CentsLoader />;
  }

  const filteredFlags = featureFlags.filter(
    ({ name, key }) =>
      name.toLowerCase().includes(flagFilter.toLowerCase()) ||
      key.toLowerCase().includes(flagFilter.toLowerCase()),
  );

  return (
    <section className={styles.flagSection}>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.search}>
        <TableSearchBar value={flagFilter} placeholder="filter flags" onSearch={setFlagFilter} />
      </div>
      <p className={styles.flagSectionDescription}>
        These are feature flags used for development, alpha/beta releases, and feature rollouts.
        Their state is stored in launch darkly. See{' '}
        <a
          href="https://cents.atlassian.net/wiki/spaces/ED/pages/213450976/Creating+Maintaining+Feature+Flags+in+LaunchDarkly"
          target="_blank"
          rel="noopener noreferrer"
        >
          our confluence doc
        </a>{' '}
        for details on how to create and manage these flags.
      </p>
      <List>
        {filteredFlags.map(({ name, state, key, description }, idx) => {
          // Use local state if present, otherwise use server state
          const checked = localFlagStates[key] !== undefined ? localFlagStates[key] : state;
          return (
            <Fragment key={`business-ld-flag-${key}`}>
              <ListItem>
                <Grid container alignItems="center">
                  <Grid item xs={5}>
                    <Typography className={styles.flagLabel}>{name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">{description}</Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Switch
                      checked={checked}
                      onChange={() => {
                        setLocalFlagStates((prev) => ({
                          ...prev,
                          [key]: !checked,
                        }));
                        saveLaunchDarklyFeatureFlagValue.mutate({
                          flagKey: key,
                          value: !checked,
                        });
                      }}
                      color="primary"
                    />
                  </Grid>
                </Grid>
              </ListItem>
              {idx !== featureFlags.length - 1 && <Divider />}
            </Fragment>
          );
        })}
      </List>
    </section>
  );
};

export default IndividualBusinessFeatureFlags;
